// ============================
// File: src/pages/DemandEditPage.jsx
// Purpose: Allow business owners to edit demand details and fix gaps after triage failure
// ============================

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getDemand, getClarifyTasks, updateDemand } from "../api/demandsClient";
import { runTriageValidate } from "../features/triage/api/triageClient";

// Add toast animation
const style = document.createElement('style');
style.textContent = `
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;
document.head.appendChild(style);

export default function DemandEditPage({ demandId: propDemandId, onSaveComplete }) {
  const { demandId: paramDemandId } = useParams();
  const navigate = useNavigate();

  // Use prop demandId if provided, otherwise use URL param
  const demandId = propDemandId || paramDemandId;
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [demand, setDemand] = useState(null);
  const [gaps, setGaps] = useState([]);
  const [formData, setFormData] = useState({});
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [resubmitting, setResubmitting] = useState(false);
  const [saved, setSaved] = useState(false);

  // Toast helper
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  // Resubmit for triage validation
  const handleResubmit = async () => {
    try {
      setResubmitting(true);
      showToast('🤖 Running AI Triage Validation...', 'success');

      // Call triage validation API
      const result = await runTriageValidate(demandId, formData);
      const triageResult = result.triage_result || result;

      if (triageResult.triage_pass) {
        showToast(`✅ Triage PASSED! Score: ${triageResult.triage_score}/100. Demand moved to Discovery.`, 'success');

        // If callback provided, go back to list after success
        if (onSaveComplete) {
          setTimeout(() => {
            onSaveComplete();
          }, 2000);
        }
      } else {
        showToast(`❌ Triage FAILED. Score: ${triageResult.triage_score}/100. ${triageResult.gap_list?.length || 0} gaps remaining.`, 'error');

        // Reload gaps
        const gapsResult = await getClarifyTasks(demandId);
        setGaps(gapsResult.tasks || []);
      }

      setResubmitting(false);
    } catch (err) {
      console.error('Error resubmitting for triage:', err);
      showToast(`❌ Error: ${err.message}`, 'error');
      setResubmitting(false);
    }
  };

  // Load demand data and gaps
  useEffect(() => {
    const loadDemand = async () => {
      try {
        setLoading(true);
        
        // Fetch demand details
        const demandResult = await getDemand(demandId);
        const demandData = demandResult.demand;
        
        setDemand(demandData);
        
        // Parse strategic tags
        let strategicTags = [];
        try {
          strategicTags = typeof demandData.strategic_tags === 'string'
            ? JSON.parse(demandData.strategic_tags)
            : (demandData.strategic_tags || []);
        } catch (e) {
          console.warn('Failed to parse strategic tags:', e);
        }
        
        // Parse expected outcomes
        let expectedOutcomes = [];
        try {
          expectedOutcomes = typeof demandData.expected_outcomes === 'string'
            ? JSON.parse(demandData.expected_outcomes)
            : (demandData.expected_outcomes || []);
        } catch (e) {
          expectedOutcomes = demandData.expected_outcomes ? [demandData.expected_outcomes] : [];
        }
        
        // Initialize form data
        setFormData({
          title: demandData.title || '',
          description: demandData.description || '',
          businessOwner: demandData.business_owner || '',
          sponsor: demandData.sponsor || '',
          directorate: demandData.directorate || '',
          strategicTags: strategicTags,
          urgency: demandData.urgency || 'Medium',
          expectedOutcomes: expectedOutcomes,
          roiBenefit: demandData.initial_roi || '',
        });
        
        // Fetch ClarifyBot tasks (gaps)
        const tasksResult = await getClarifyTasks(demandId);
        setGaps(tasksResult.tasks || []);
        
        setLoading(false);
      } catch (err) {
        console.error('Error loading demand:', err);
        setError(err.message);
        setLoading(false);
      }
    };
    
    if (demandId) {
      loadDemand();
    }
  }, [demandId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    console.log('💾 Saving demand with data:', formData);

    try {
      await updateDemand(demandId, formData);

      showToast('✅ Demand updated successfully! Now resubmit for triage validation.', 'success');
      setSaved(true);
      
    } catch (err) {
      console.error('Error saving demand:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading demand details...</p>
        </div>
      </div>
    );
  }

  if (error && !demand) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 mb-4">❌ Error: {error}</p>
          <button
            onClick={() => navigate('/demand-dashboard')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Edit Demand: {demand?.title}
          </h1>
          <p className="text-gray-600">
            Demand ID: <span className="font-mono font-semibold">{demandId}</span>
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Status: <span className="font-semibold text-orange-600">{demand?.current_stage}</span>
          </p>
        </div>

        {/* Workflow Guide */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
          <h2 className="text-lg font-bold text-blue-900 mb-3 flex items-center gap-2">
            <span style={{ fontSize: '24px' }}>📋</span>
            How to Fix and Resubmit
          </h2>
          <ol className="space-y-2 text-sm text-blue-900">
            <li className="flex items-start gap-2">
              <span className="font-bold min-w-[24px]">1.</span>
              <span>Review the gaps listed below and understand what needs to be fixed</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold min-w-[24px]">2.</span>
              <span>Edit the form fields to address each issue (follow the suggestions)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold min-w-[24px]">3.</span>
              <span>Click "💾 Save Changes" to update the demand</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold min-w-[24px]">4.</span>
              <span>Click "🔄 Resubmit for Triage" to run AI validation again</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold min-w-[24px]">5.</span>
              <span>If all gaps are fixed, the demand will pass (score ≥ 70) and move to Discovery!</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold min-w-[24px]">6.</span>
              <span>If gaps remain, review the updated gap list and fix them</span>
            </li>
          </ol>
        </div>

        {/* Gaps Section */}
        {gaps.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
            <h2 className="text-lg font-bold text-red-900 mb-4">
              ⚠️ Issues to Fix ({gaps.length})
            </h2>
            <div className="space-y-3">
              {gaps.map((gap, idx) => (
                <div key={idx} className="bg-white rounded-lg p-4 border border-red-300">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900 mb-1">
                        Field: <span className="text-red-600">{gap.field_name}</span>
                      </p>
                      <p className="text-sm text-gray-700 mb-2">
                        <strong>Issue:</strong> {gap.issue_description}
                      </p>
                      {gap.suggestion && (
                        <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                          <strong>Suggestion:</strong> {gap.suggestion}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Edit Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Edit Demand Details</h2>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-800">❌ {error}</p>
            </div>
          )}

          <div className="space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
                minLength={6}
              />
              <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={6}
                required
                minLength={20}
              />
              <p className="text-xs text-gray-500 mt-1">Minimum 20 characters - provide detailed context</p>
            </div>

            {/* Business Owner */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Business Owner *
              </label>
              <input
                type="text"
                value={formData.businessOwner}
                onChange={(e) => setFormData({...formData, businessOwner: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
                minLength={3}
              />
            </div>

            {/* Sponsor */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Sponsor *
              </label>
              <input
                type="text"
                value={formData.sponsor}
                onChange={(e) => setFormData({...formData, sponsor: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
                minLength={3}
              />
            </div>

            {/* Directorate */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Directorate *
              </label>
              <input
                type="text"
                value={formData.directorate}
                onChange={(e) => setFormData({...formData, directorate: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
                minLength={2}
              />
            </div>

            {/* Strategic Tags */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Strategic Tags * (Select at least one)
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {["ACF", "OCIO", "HHS", "Zero Trust", "CX", "Data", "AI/ML", "Cloud", "Cybersecurity"].map(tag => (
                  <label key={tag} className="flex items-center gap-2 p-3 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.strategicTags.includes(tag)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData({...formData, strategicTags: [...formData.strategicTags, tag]});
                        } else {
                          setFormData({...formData, strategicTags: formData.strategicTags.filter(t => t !== tag)});
                        }
                      }}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-sm font-medium text-gray-700">{tag}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Urgency */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Urgency *
              </label>
              <select
                value={formData.urgency}
                onChange={(e) => setFormData({...formData, urgency: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
            </div>

            {/* Expected Outcomes */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Expected Outcomes * (One per line)
              </label>
              <textarea
                value={Array.isArray(formData.expectedOutcomes) ? formData.expectedOutcomes.join('\n') : formData.expectedOutcomes}
                onChange={(e) => {
                  const outcomes = e.target.value.split('\n').filter(line => line.trim());
                  setFormData({...formData, expectedOutcomes: outcomes});
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={4}
                required
                placeholder="Enter measurable success criteria, one per line&#10;Example:&#10;- Reduce processing time by 30%&#10;- Increase user satisfaction to 85%&#10;- Automate 50% of manual tasks"
              />
              <p className="text-xs text-gray-500 mt-1">Provide specific, measurable outcomes</p>
            </div>

            {/* ROI Benefit */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                ROI / Business Benefit
              </label>
              <textarea
                value={formData.roiBenefit}
                onChange={(e) => setFormData({...formData, roiBenefit: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={3}
                placeholder="Describe the expected return on investment or business benefit"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 pt-6 border-t">
              <button
                type="submit"
                disabled={saving}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : '💾 Save Changes'}
              </button>

              {saved && (
                <button
                  type="button"
                  onClick={handleResubmit}
                  disabled={resubmitting}
                  className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {resubmitting ? '⏳ Validating...' : '🔄 Resubmit for Triage'}
                </button>
              )}

              <button
                type="button"
                onClick={() => onSaveComplete ? onSaveComplete() : navigate(`/triage/${demandId}`)}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300"
              >
                {onSaveComplete ? '← Back to Tasks' : 'Cancel'}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Toast Notification */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            top: '24px',
            right: '24px',
            backgroundColor: toast.type === 'success' ? 'var(--cares-green)' : 'var(--cares-orange)',
            color: 'var(--cares-text-max)',
            padding: '16px 24px',
            borderRadius: '8px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            zIndex: 10000,
            maxWidth: '500px',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            animation: 'slideIn 0.3s ease-out',
          }}
        >
          <span style={{ fontSize: '20px' }}>
            {toast.type === 'success' ? '✅' : '❌'}
          </span>
          <span>{toast.message}</span>
          <button
            onClick={() => setToast(null)}
            style={{
              marginLeft: 'auto',
              background: 'none',
              border: 'none',
              color: 'var(--cares-text-max)',
              fontSize: '20px',
              cursor: 'pointer',
              padding: '0 4px',
              opacity: 0.8,
            }}
            onMouseEnter={(e) => e.target.style.opacity = '1'}
            onMouseLeave={(e) => e.target.style.opacity = '0.8'}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}

